import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { EventManagerWrapper } from '../../EventComponents/EventManagerWrapper';
import { EventCoverImage } from '../../EventComponents/EventCoverImage';
import SegmentedControl from '../../SegmentedControl/SegmentedControl';
import { IPagination } from '../../../hook/usePagination';
import { EventDetailsTab } from './Tabs/EventDetailsTab';
import { EventDiscountsTab } from './Tabs/EventDiscountsTab';
import { EventSalesTab } from './Tabs/EventSalesTab';
import { EventTicketsTab } from './Tabs/EventTicketsTab';
import { EventAnalyticsTab } from './Tabs/EventAnalyticsTab';
import { EventFeesTab } from './Tabs/EventFeesTab';
import { EventPromotionsTab } from './Tabs/EventPromotionsTab';
import { IEvent, IRawFeeStructure } from '../../../types/global/helper';
import {
    IManagedEventSales,
    IManagedEventTickets,
    IManagedEventDiscount,
    IManagedEventTicket,
} from '../../../redux/events/types';
import CustomButton from '../../Button/CustomButton';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';
import { useDispatch } from 'react-redux';
import { publishEvent, unpublishEvent } from '../../../redux/events/action';
import { CustomizedSwitches } from '../../Switch/Switch';

export interface IEventManageContainerProps {
    event?: IEvent;
    sales?: IManagedEventSales;
    tickets?: IManagedEventTickets;
    discounts?: IManagedEventDiscount;
    totalAttendees?: number;
    loading: boolean;
    error: any;
    activeTab: string;
    salePagination: IPagination;
    ticketPagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearchChange: (searchText: string) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onTicketTypeChange: (type: string) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onTabChange: (tab: string) => void;
    onSaleRowsChange: (rows: number) => void;
    onSalePageChange: (page: number) => void;
    onTicketRowsChange: (rows: number) => void;
    onTicketPageChange: (page: number) => void;
    onEditDetails: (sectionName: string) => void;
    onEditDiscount: (discountId: string) => void;
    onCreateDiscount: () => void;
    onEditFee: (fee: IRawFeeStructure) => void;
    onDeleteFee: (feeId: string) => void;
    onAddFee: (fee: any) => void;
    onExportCSV: (type: 'sales' | 'tickets', data: IManagedEventSales | IManagedEventTicket[]) => void;
    onClearSearch: () => void;
    onClearStartDate: () => void;
    onClearEndDate: () => void;
    onClearTicketType: () => void;
    onClearAllFilters: () => void;
    onClearError: () => void;
    onImageChange?: (image: string, rawFile: File) => void;
    updatingInProgress?: boolean;
    onCancelEvent?: () => void;
}

export const EventManageContainer: React.FC<IEventManageContainerProps> = ({
    event,
    sales,
    tickets,
    discounts,
    totalAttendees,
    loading,
    error,
    activeTab,
    salePagination,
    ticketPagination,
    sortOrder,
    onSearchChange,
    onStartDateChange,
    onEndDateChange,
    onTicketTypeChange,
    onSortOrderChange,
    onTabChange,
    onSaleRowsChange,
    onSalePageChange,
    onTicketRowsChange,
    onTicketPageChange,
    onEditDetails,
    onEditDiscount,
    onCreateDiscount,
    onEditFee,
    onDeleteFee,
    onAddFee,
    onExportCSV,
    onClearSearch,
    onClearStartDate,
    onClearEndDate,
    onClearTicketType,
    onClearAllFilters,
    onClearError,
    onImageChange,
    updatingInProgress,
    onCancelEvent,
}) => {
    const { themeStyle, device } = useThemeContext();
    const location = useLocation();
    const navigate = useNavigate();
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [cannotUnpublishDialogOpen, setCannotUnpublishDialogOpen] = useState(false);
    const dispatch = useDispatch();

    const tabs = useMemo(
        () => [
            { value: 'details', label: 'Details' },
            { value: 'discounts', label: 'Discounts' },
            { value: 'sales', label: 'Sales' },
            { value: 'fees', label: 'Fees' },
            { value: 'tickets', label: 'Tickets' },
            { value: 'analytics', label: 'Analytics' },
            { value: 'promotions', label: 'Promotions' },
        ],
        [],
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromQuery = params.get('tab');
        const defaultTabValue = tabs[0].value;

        const targetTab = tabs.find(t => t.value === tabFromQuery)?.value || defaultTabValue;

        if (targetTab !== activeTab) {
            onTabChange(targetTab);
        }

        if (params.get('tab') !== targetTab) {
            navigate({ search: `?tab=${targetTab}` }, { replace: true });
        }
    }, [location.search, activeTab, onTabChange, navigate, tabs]);

    const handleSegmentChange = (tabId: string) => {
        onTabChange(tabId);
        navigate({ search: `?tab=${tabId}` }, { replace: true });
    };

    const handleCancelClick = () => {
        setShowCancelDialog(true);
    };

    const handleCancelConfirm = () => {
        onCancelEvent?.();
        setShowCancelDialog(false);
    };

    const handleCancelCancel = () => {
        setShowCancelDialog(false);
    };

    // --- PUBLISH/UNPUBLISH SWITCH LOGIC ---
    const handlePublishStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!event) return;
        const isPublishing = e.target.checked;
        // Try to get tickets sold from event object, fallback to 0 if not present
        let ticketsSold = tickets?.total || 0;

        if (isPublishing) {
            if (ticketsSold > 0 && event.state === 'active') {
                setPublishDialogOpen(true);
            } else {
                dispatch(publishEvent({ documentId: event._id }));
            }
        } else {
            if (ticketsSold > 0 && event.state === 'active') {
                setCannotUnpublishDialogOpen(true);
            } else {
                dispatch(unpublishEvent({ documentId: event._id }));
            }
        }
    };

    const renderPublishedStatus = useMemo(() => {
        return (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <CustomizedSwitches
                    switchProps={{
                        checked: event?.status === 'published',
                        onChange: handlePublishStatusChange,
                    }}
                    label={event?.status === 'published' ? 'Published' : 'Draft'}
                    style={{
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}
                />
            </Box>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.status, themeStyle]);

    if (loading && !event) {
        return (
            <Container
                disableGutters={device.isMobile}
                sx={{
                    py: 4,
                    overflow: 'hidden',
                    mr: {
                        xs: 0,
                        sm: 4,
                    },
                    p: { xs: 2 },
                    ml: { xs: 0, sm: 10 },
                    width: { xs: '100%', sm: '85%', smMd: '90%', xl: '95%' },
                }}>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container
                disableGutters={device.isMobile}
                sx={{
                    height: 'auto',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: { xs: '100%', sm: '85%', smMd: '90%', xl: '95%' },
                    margin: 10,
                }}>
                <Box
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].table_header,
                        textAlign: 'flex-start',
                        maxWidth: '500px',
                        width: '100%',
                    }}>
                    <Typography
                        color="error"
                        sx={{
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.2rem',
                        }}>
                        Error loading event details
                    </Typography>
                    <Typography
                        sx={{
                            mb: 3,
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        We couldn't load the event details at this time. Please try again.
                    </Typography>
                    <CustomButton
                        onClick={onClearError}
                        label="Retry"
                        customStyle={{
                            width: '100%',
                            borderRadius: '10px',
                        }}
                    />
                </Box>
            </Container>
        );
    }

    return (
        <Container
            disableGutters
            sx={{
                height: 'auto',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                width: { xs: '100%', sm: '85%', smMd: '90%', xl: '95%' },
                // overflow: 'hidden',
            }}>
            <EventManagerWrapper loading={updatingInProgress}>
                {/* Hero Section with Cover Image */}
                {event && <EventCoverImage event={event} editMode onImageChange={onImageChange} />}
                {event && renderPublishedStatus}

                {/* Stats Summary */}
                <Box sx={{ mb: 4, mt: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between">
                        <Box
                            sx={{
                                backgroundColor: theme[themeStyle].table_header,
                                borderRadius: 2,
                                padding: 2,
                                flex: 1,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '1rem',
                                    color: theme[themeStyle].light_gray_text,
                                }}>
                                Total Sales
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '1.75rem',
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {sales?.total?.formatted_amount || '$0.00'}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: theme[themeStyle].table_header,
                                borderRadius: 2,
                                padding: 2,
                                flex: 1,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '1rem',
                                    color: theme[themeStyle].light_gray_text,
                                }}>
                                Total Attendees
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '1.75rem',
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {totalAttendees || 0}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* Segmented Control for Tabs */}
                <Box sx={{ mb: 4 }}>
                    <SegmentedControl
                        initialValue={activeTab}
                        options={tabs}
                        onChange={handleSegmentChange}
                        fullWidth
                    />
                </Box>

                {/* Tab Content */}
                <Box sx={{ mt: 4 }}>
                    {activeTab === 'details' && <EventDetailsTab event={event} onEditDetails={onEditDetails} />}
                    {activeTab === 'discounts' && <EventDiscountsTab discounts={discounts} eventId={event?._id!} />}
                    {activeTab === 'sales' && (
                        <EventSalesTab
                            sales={sales}
                            pagination={salePagination}
                            sortOrder={sortOrder}
                            onSearchChange={onSearchChange}
                            onStartDateChange={onStartDateChange}
                            onEndDateChange={onEndDateChange}
                            onSortOrderChange={onSortOrderChange}
                            onRowsChange={onSaleRowsChange}
                            onPageChange={onSalePageChange}
                            onClearSearch={onClearSearch}
                            onClearStartDate={onClearStartDate}
                            onClearEndDate={onClearEndDate}
                            onExportCSV={sales => onExportCSV('sales', sales as IManagedEventSales)}
                        />
                    )}
                    {activeTab === 'fees' && (
                        <EventFeesTab
                            fees={(event?.fee_structure as unknown as IRawFeeStructure[]) || []}
                            onAddFee={fee => {
                                onAddFee(fee);
                            }}
                            onEditFee={fee => {
                                onEditFee(fee);
                            }}
                            onDeleteFee={onDeleteFee}
                        />
                    )}
                    {activeTab === 'tickets' && (
                        <EventTicketsTab
                            tickets={tickets?.data || []}
                            pagination={ticketPagination}
                            onSearchChange={onSearchChange}
                            onTicketTypeChange={onTicketTypeChange}
                            onSortOrderChange={onSortOrderChange}
                            onRowsChange={onTicketRowsChange}
                            onPageChange={onTicketPageChange}
                            onClearSearch={onClearSearch}
                            onClearTicketType={onClearTicketType}
                            onExportCSV={tickets => onExportCSV('tickets', tickets as IManagedEventTicket[])}
                        />
                    )}
                    {activeTab === 'analytics' && <EventAnalyticsTab />}
                    {activeTab === 'promotions' && <EventPromotionsTab />}
                </Box>

                {/* Danger Zone Section */}
                {event?.state !== 'cancelled' && (
                    <Box
                        sx={{
                            mt: 4,
                            p: 3,
                            borderRadius: 2,
                            border: `1px solid ${theme[themeStyle].primary_red}`,
                            backgroundColor: theme[themeStyle].table_header,
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].primary_red,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.2rem',
                                mb: 2,
                            }}>
                            Danger Zone
                        </Typography>
                        <Typography
                            sx={{
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                mb: 3,
                            }}>
                            Once you cancel an event, this action cannot be undone. Please be certain.
                        </Typography>
                        <CustomButton
                            label="Cancel Event"
                            onClick={handleCancelClick}
                            customStyle={{
                                backgroundColor: theme[themeStyle].primary_red,
                                opacity: 1,
                                borderRadius: '5px',
                            }}
                            sx={{
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_red,
                                    opacity: 0.8,
                                },
                            }}
                        />
                    </Box>
                )}

                <DialogueSheet
                    open={showCancelDialog}
                    onClose={handleCancelCancel}
                    onAgreeClicked={handleCancelConfirm}
                    onDisagreeClick={handleCancelCancel}
                    header="Cancel Event"
                    body="Are you sure you want to cancel this event? This action cannot be undone."
                    positiveText="Yes, Cancel Event"
                    negativeText="No, Keep Event"
                    negtiveTextStyle={{
                        color: theme[themeStyle].primary_red,
                    }}
                />
                {/* Publish Confirmation Dialog */}
                <DialogueSheet
                    open={publishDialogOpen}
                    onClose={() => setPublishDialogOpen(false)}
                    onAgreeClicked={() => {
                        if (event?._id) {
                            dispatch(publishEvent({ documentId: event._id }));
                        }
                        setPublishDialogOpen(false);
                    }}
                    onDisagreeClick={() => setPublishDialogOpen(false)}
                    header="Publish Event"
                    body={`This event has sold tickets. Are you sure you want to publish it?`}
                    positiveText="Publish"
                    negativeText="Cancel"
                    negtiveTextStyle={{
                        color: theme[themeStyle].tag_white,
                    }}
                />
                {/* Cannot Unpublish Dialog */}
                <DialogueSheet
                    open={cannotUnpublishDialogOpen}
                    onClose={() => setCannotUnpublishDialogOpen(false)}
                    onAgreeClicked={() => setCannotUnpublishDialogOpen(false)}
                    onDisagreeClick={() => setCannotUnpublishDialogOpen(false)}
                    header="Cannot Unpublish Event"
                    body={
                        'You cannot unpublish this event because tickets have already been sold. To unpublish, you must cancel the event, which will process refunds for all sold tickets.'
                    }
                    positiveText="OK"
                    negativeText="Cancel"
                    negtiveTextStyle={{
                        color: theme[themeStyle].primary_red,
                    }}
                />
            </EventManagerWrapper>
        </Container>
    );
};
